import React, { useState, createElement } from 'react';
import { StatsCard } from './shared/StatsCard';
import { SegmentDropdown } from './shared/SegmentDropdown';
import { Chart } from './shared/Chart';
import { Download, DollarSign, Droplet, Fish, Wallet, AlertCircle, Brain, Package, Mic, Glasses, Shirt, Pill, Zap, Info, Bot, User, TrendingUp, BarChart } from 'lucide-react';
// User segments
const segments = [{
  id: 'all',
  label: 'All Users'
}, {
  id: 'whales',
  label: 'Whales',
  icon: <DollarSign size={16} className="text-emerald-500" data-id="element-14751" />,
  customerClass: 'VIP'
}, {
  id: 'dolphins',
  label: 'Dolphins',
  icon: <Droplet size={16} className="text-blue-500" data-id="element-14752" />,
  customerClass: 'Spender'
}, {
  id: 'minnows',
  label: 'Minnows',
  icon: <Fish size={16} className="text-cyan-500" data-id="element-14753" />,
  customerClass: 'Low Spender'
}, {
  id: 'non-spenders',
  label: 'Non-Spenders',
  icon: <Wallet size={16} className="text-gray-500" data-id="element-14754" />,
  customerClass: 'Newbie'
}, {
  id: 'churn-risks',
  label: 'Churn Risks',
  icon: <AlertCircle size={16} className="text-red-500" data-id="element-14755" />,
  customerClass: 'Dormant'
}];
// Upsell types
const upsellTypes = [{
  id: 'toys',
  name: 'Interactive Toys',
  icon: <Package size={16} className="text-pink-500" data-id="element-14756" />,
  attachRate: {
    whales: 32,
    dolphins: 18,
    minnows: 8,
    'non-spenders': 0.5,
    'churn-risks': 5
  },
  rpm: {
    whales: 12.5,
    dolphins: 8.2,
    minnows: 4.1,
    'non-spenders': 0.8,
    'churn-risks': 3.5
  },
  algorithmConfidence: {
    whales: 95,
    dolphins: 88,
    minnows: 75,
    'non-spenders': 45,
    'churn-risks': 82
  },
  fallbackMethod: {
    whales: 'lookalike',
    dolphins: 'lookalike',
    minnows: 'broad',
    'non-spenders': 'context',
    'churn-risks': 'persona'
  },
  active: {
    whales: true,
    dolphins: true,
    minnows: true,
    'non-spenders': false,
    'churn-risks': true
  },
  revenue: 245000,
  aiRevenue: 73500,
  humanRevenue: 171500,
  bestFor: 'VIP'
}, {
  id: 'voice',
  name: 'AI Voice Layer',
  icon: <Mic size={16} className="text-indigo-500" data-id="element-14757" />,
  attachRate: {
    whales: 45,
    dolphins: 28,
    minnows: 12,
    'non-spenders': 1.2,
    'churn-risks': 8
  },
  rpm: {
    whales: 9.8,
    dolphins: 6.5,
    minnows: 3.2,
    'non-spenders': 0.5,
    'churn-risks': 2.8
  },
  algorithmConfidence: {
    whales: 92,
    dolphins: 85,
    minnows: 72,
    'non-spenders': 40,
    'churn-risks': 78
  },
  fallbackMethod: {
    whales: 'lookalike',
    dolphins: 'lookalike',
    minnows: 'broad',
    'non-spenders': 'context',
    'churn-risks': 'persona'
  },
  active: {
    whales: true,
    dolphins: true,
    minnows: true,
    'non-spenders': true,
    'churn-risks': true
  },
  revenue: 185000,
  aiRevenue: 129500,
  humanRevenue: 55500,
  bestFor: 'Spender'
}, {
  id: 'ar-vr',
  name: 'AR/VR Experiences',
  icon: <Glasses size={16} className="text-blue-500" data-id="element-14758" />,
  attachRate: {
    whales: 28,
    dolphins: 15,
    minnows: 5,
    'non-spenders': 0.2,
    'churn-risks': 3
  },
  rpm: {
    whales: 15.2,
    dolphins: 10.5,
    minnows: 5.8,
    'non-spenders': 1.2,
    'churn-risks': 4.5
  },
  algorithmConfidence: {
    whales: 90,
    dolphins: 82,
    minnows: 68,
    'non-spenders': 35,
    'churn-risks': 75
  },
  fallbackMethod: {
    whales: 'lookalike',
    dolphins: 'lookalike',
    minnows: 'broad',
    'non-spenders': 'context',
    'churn-risks': 'broad'
  },
  active: {
    whales: true,
    dolphins: true,
    minnows: false,
    'non-spenders': false,
    'churn-risks': false
  },
  revenue: 168000,
  aiRevenue: 33600,
  humanRevenue: 134400,
  bestFor: 'VIP'
}, {
  id: 'skins',
  name: 'Angel Skins',
  icon: <Shirt size={16} className="text-amber-500" data-id="element-14759" />,
  attachRate: {
    whales: 58,
    dolphins: 42,
    minnows: 25,
    'non-spenders': 3.5,
    'churn-risks': 12
  },
  rpm: {
    whales: 7.8,
    dolphins: 5.2,
    minnows: 2.5,
    'non-spenders': 0.4,
    'churn-risks': 1.8
  },
  algorithmConfidence: {
    whales: 96,
    dolphins: 90,
    minnows: 82,
    'non-spenders': 55,
    'churn-risks': 85
  },
  fallbackMethod: {
    whales: 'lookalike',
    dolphins: 'lookalike',
    minnows: 'lookalike',
    'non-spenders': 'broad',
    'churn-risks': 'lookalike'
  },
  active: {
    whales: true,
    dolphins: true,
    minnows: true,
    'non-spenders': true,
    'churn-risks': true
  },
  revenue: 210000,
  aiRevenue: 147000,
  humanRevenue: 63000,
  bestFor: 'Low Spender'
}, {
  id: 'supplements',
  name: 'Supplements',
  icon: <Pill size={16} className="text-green-500" data-id="element-14760" />,
  attachRate: {
    whales: 15,
    dolphins: 8,
    minnows: 3,
    'non-spenders': 0.1,
    'churn-risks': 2
  },
  rpm: {
    whales: 18.5,
    dolphins: 12.8,
    minnows: 6.5,
    'non-spenders': 1.5,
    'churn-risks': 5.2
  },
  algorithmConfidence: {
    whales: 88,
    dolphins: 80,
    minnows: 65,
    'non-spenders': 30,
    'churn-risks': 72
  },
  fallbackMethod: {
    whales: 'lookalike',
    dolphins: 'lookalike',
    minnows: 'context',
    'non-spenders': 'context',
    'churn-risks': 'persona'
  },
  active: {
    whales: true,
    dolphins: true,
    minnows: false,
    'non-spenders': false,
    'churn-risks': true
  },
  revenue: 125000,
  aiRevenue: 25000,
  humanRevenue: 100000,
  bestFor: 'Dormant'
}];
// Fallback methods
const fallbackMethods = [{
  id: 'lookalike',
  label: 'Lookalike Audiences'
}, {
  id: 'broad',
  label: 'Broad Targeting'
}, {
  id: 'context',
  label: 'Context Fallback'
}, {
  id: 'persona',
  label: 'Persona Fallback'
}];
// Chart data for attach rates
const attachRateData = [{
  name: 'Whales',
  toys: upsellTypes.find(u => u.id === 'toys')?.attachRate.whales || 0,
  voice: upsellTypes.find(u => u.id === 'voice')?.attachRate.whales || 0,
  'ar-vr': upsellTypes.find(u => u.id === 'ar-vr')?.attachRate.whales || 0,
  skins: upsellTypes.find(u => u.id === 'skins')?.attachRate.whales || 0,
  supplements: upsellTypes.find(u => u.id === 'supplements')?.attachRate.whales || 0
}, {
  name: 'Dolphins',
  toys: upsellTypes.find(u => u.id === 'toys')?.attachRate.dolphins || 0,
  voice: upsellTypes.find(u => u.id === 'voice')?.attachRate.dolphins || 0,
  'ar-vr': upsellTypes.find(u => u.id === 'ar-vr')?.attachRate.dolphins || 0,
  skins: upsellTypes.find(u => u.id === 'skins')?.attachRate.dolphins || 0,
  supplements: upsellTypes.find(u => u.id === 'supplements')?.attachRate.dolphins || 0
}, {
  name: 'Minnows',
  toys: upsellTypes.find(u => u.id === 'toys')?.attachRate.minnows || 0,
  voice: upsellTypes.find(u => u.id === 'voice')?.attachRate.minnows || 0,
  'ar-vr': upsellTypes.find(u => u.id === 'ar-vr')?.attachRate.minnows || 0,
  skins: upsellTypes.find(u => u.id === 'skins')?.attachRate.minnows || 0,
  supplements: upsellTypes.find(u => u.id === 'supplements')?.attachRate.minnows || 0
}];
// Chart data for RPM
const rpmData = [{
  name: 'Whales',
  toys: upsellTypes.find(u => u.id === 'toys')?.rpm.whales || 0,
  voice: upsellTypes.find(u => u.id === 'voice')?.rpm.whales || 0,
  'ar-vr': upsellTypes.find(u => u.id === 'ar-vr')?.rpm.whales || 0,
  skins: upsellTypes.find(u => u.id === 'skins')?.rpm.whales || 0,
  supplements: upsellTypes.find(u => u.id === 'supplements')?.rpm.whales || 0
}, {
  name: 'Dolphins',
  toys: upsellTypes.find(u => u.id === 'toys')?.rpm.dolphins || 0,
  voice: upsellTypes.find(u => u.id === 'voice')?.rpm.dolphins || 0,
  'ar-vr': upsellTypes.find(u => u.id === 'ar-vr')?.rpm.dolphins || 0,
  skins: upsellTypes.find(u => u.id === 'skins')?.rpm.dolphins || 0,
  supplements: upsellTypes.find(u => u.id === 'supplements')?.rpm.dolphins || 0
}, {
  name: 'Minnows',
  toys: upsellTypes.find(u => u.id === 'toys')?.rpm.minnows || 0,
  voice: upsellTypes.find(u => u.id === 'voice')?.rpm.minnows || 0,
  'ar-vr': upsellTypes.find(u => u.id === 'ar-vr')?.rpm.minnows || 0,
  skins: upsellTypes.find(u => u.id === 'skins')?.rpm.minnows || 0,
  supplements: upsellTypes.find(u => u.id === 'supplements')?.rpm.minnows || 0
}];
// Revenue split data
const revenueSplitData = upsellTypes.map(upsell => ({
  name: upsell.name,
  AI: upsell.aiRevenue,
  Human: upsell.humanRevenue
}));
export const UpsellLayers: React.FC = () => {
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [upsellActive, setUpsellActive] = useState(upsellTypes.reduce((acc, upsell) => ({
    ...acc,
    [upsell.id]: upsell.active
  }), {}));
  const [fallbackSettings, setFallbackSettings] = useState(upsellTypes.reduce((acc, upsell) => ({
    ...acc,
    [upsell.id]: upsell.fallbackMethod
  }), {}));
  const [exportSuccess, setExportSuccess] = useState(false);
  const handleToggleUpsell = (upsellId: string, segment: string) => {
    setUpsellActive(prev => ({
      ...prev,
      [upsellId]: {
        ...prev[upsellId],
        [segment]: !prev[upsellId][segment]
      }
    }));
  };
  const handleChangeFallback = (upsellId: string, segment: string, method: string) => {
    setFallbackSettings(prev => ({
      ...prev,
      [upsellId]: {
        ...prev[upsellId],
        [segment]: method
      }
    }));
  };
  const handleExportData = () => {
    // Prepare the data for export
    const exportData = upsellTypes.map(upsell => ({
      'Upsell Type': upsell.name,
      'Total Revenue': `$${upsell.revenue.toLocaleString()}`,
      'AI Revenue': `$${upsell.aiRevenue.toLocaleString()}`,
      'Human Revenue': `$${upsell.humanRevenue.toLocaleString()}`,
      'AI Commission Fee': `$${(upsell.aiRevenue * 0.15).toLocaleString()}`,
      'Best For': upsell.bestFor,
      'Algorithm Confidence': `${Object.values(upsell.algorithmConfidence).reduce((sum, val) => sum + val, 0) / Object.values(upsell.algorithmConfidence).length}%`
    }));
    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvContent = [headers.join(','), ...exportData.map(row => headers.map(header => JSON.stringify(row[header] || '')).join(','))].join('\n');
    // Create a blob and download link
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'upsell_data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Show success message
    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 3000);
  };
  const totalRevenue = upsellTypes.reduce((sum, upsell) => sum + upsell.revenue, 0);
  const totalAiRevenue = upsellTypes.reduce((sum, upsell) => sum + upsell.aiRevenue, 0);
  const totalHumanRevenue = upsellTypes.reduce((sum, upsell) => sum + upsell.humanRevenue, 0);
  const aiPercentage = Math.round(totalAiRevenue / totalRevenue * 100);
  const humanPercentage = 100 - aiPercentage;
  // Calculate AI commission
  const aiCommissionRate = 0.15; // 15% commission rate for AI
  const aiCommission = totalAiRevenue * aiCommissionRate;
  const avgAttachRate = (segment: string) => {
    const rates = upsellTypes.map(upsell => upsell.attachRate[segment] || 0);
    return rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
  };
  const avgRPM = (segment: string) => {
    const rpms = upsellTypes.map(upsell => upsell.rpm[segment] || 0);
    return rpms.reduce((sum, rpm) => sum + rpm, 0) / rpms.length;
  };
  return <div data-id="element-14761">
      <div className="flex items-center justify-between mb-6" data-id="element-14762">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-14763">
          Upsell Layers
        </h2>
        <div className="flex items-center gap-3" data-id="element-14764">
          <SegmentDropdown label="" options={segments} value={selectedSegment} onChange={setSelectedSegment} className="w-48" data-id="element-14765" />
          <button onClick={handleExportData} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-14766">
            <Download size={16} data-id="element-14767" />
            <span data-id="element-14768">Export Data</span>
          </button>
          {exportSuccess && <div className="absolute top-4 right-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-4 py-2 rounded-md shadow-md" data-id="element-14769">
              Data exported successfully!
            </div>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-14770">
        <StatsCard title="Total Upsell Revenue" value={`$${(totalRevenue / 1000).toFixed(1)}K`} change={18.5} changeLabel="vs last month" icon={<Zap size={16} className="text-amber-500" data-id="element-14772" />} className="bg-gradient-to-br from-white to-amber-50 dark:from-gray-800 dark:to-amber-900/20" data-id="element-14771" />
        <StatsCard title="Avg. Whale Attach Rate" value={`${avgAttachRate('whales').toFixed(1)}%`} change={5.2} changeLabel="vs last month" icon={<DollarSign size={16} className="text-emerald-500" data-id="element-14774" />} className="bg-gradient-to-br from-white to-emerald-50 dark:from-gray-800 dark:to-emerald-900/20" data-id="element-14773" />
        <StatsCard title="Avg. Dolphin Attach Rate" value={`${avgAttachRate('dolphins').toFixed(1)}%`} change={3.8} changeLabel="vs last month" icon={<Droplet size={16} className="text-blue-500" data-id="element-14776" />} data-id="element-14775" />
        <StatsCard title="Algorithm Confidence" value="82%" change={4.5} changeLabel="vs last month" icon={<Brain size={16} className="text-purple-500" data-id="element-14778" />} className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20" data-id="element-14777" />
      </div>

      {/* Revenue Split and AI Commission Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-14779">
        <div className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20 p-4 rounded-lg shadow-sm" data-id="element-14780">
          <div className="flex justify-between items-start" data-id="element-14781">
            <div data-id="element-14782">
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center group relative" data-id="element-14783">
                Revenue Split (AI vs Human)
                <Info size={14} className="ml-1 text-gray-400 cursor-help" data-id="element-14784" />
                <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-white dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-14785">
                  AI revenue is generated via automated chat, upsells, and
                  campaigns. Human revenue is generated by creators or
                  moderators.
                </span>
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-14786">
                {aiPercentage}% / {humanPercentage}%
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-14787">
                ${totalAiRevenue.toLocaleString()} / $
                {totalHumanRevenue.toLocaleString()}
              </p>
            </div>
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg" data-id="element-14788">
              <Zap className="w-5 h-5 text-purple-500 dark:text-purple-400" data-id="element-14789" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-14790">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-14791" />
            <span className="text-green-500 font-medium" data-id="element-14792">11.2%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-14793">
              AI growth vs last month
            </span>
          </div>
        </div>
        <div className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-indigo-900/20 p-4 rounded-lg shadow-sm" data-id="element-14794">
          <div className="flex justify-between items-start" data-id="element-14795">
            <div data-id="element-14796">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-14797">
                AI Commission Fee
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-14798">
                ${aiCommission.toLocaleString()}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-14799">
                Free: 15%, $30: 10%, $100: 8.5%
              </p>
            </div>
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg" data-id="element-14800">
              <DollarSign className="w-5 h-5 text-indigo-500 dark:text-indigo-400" data-id="element-14801" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-14802">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-14803" />
            <span className="text-green-500 font-medium" data-id="element-14804">9.5%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-14805">
              vs last month
            </span>
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800 p-4" data-id="element-14806">
          <div className="flex items-start" data-id="element-14807">
            <Zap className="w-5 h-5 text-blue-500 mr-2 mt-0.5" data-id="element-14808" />
            <div data-id="element-14809">
              <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-14810">
                Hybrid Routing Logic
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-14811">
                AI handles all new and low-value customers. After $120 lifetime
                spend, customer is routed to Human operator for personalized
                attention. High-value upsells are prioritized for human
                operators.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-14812">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14813">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14814">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14815">
              <Zap size={18} className="text-amber-500 mr-2" data-id="element-14816" />
              Attach Rate by Segment
            </h3>
          </div>
          <div className="p-6" data-id="element-14817">
            <div className="h-80" data-id="element-14818">
              <Chart title="" data={attachRateData} type="bar" height={300} xAxisLabel="Segment" yAxisLabel="Attach Rate (%)" data-id="element-14819" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14820">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14821">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14822">
              <DollarSign size={18} className="text-green-500 mr-2" data-id="element-14823" />
              RPM by Segment
            </h3>
          </div>
          <div className="p-6" data-id="element-14824">
            <div className="h-80" data-id="element-14825">
              <Chart title="" data={rpmData} type="bar" height={300} xAxisLabel="Segment" yAxisLabel="RPM ($)" data-id="element-14826" />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Split Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-14827">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14828">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14829">
            <BarChart size={18} className="text-purple-500 mr-2" data-id="element-14830" />
            Revenue Split by Upsell Type (AI vs Human)
          </h3>
        </div>
        <div className="p-6" data-id="element-14831">
          <div className="h-80" data-id="element-14832">
            <Chart title="" data={revenueSplitData} type="bar" height={300} yAxisLabel="Revenue ($)" data-id="element-14833" />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-14834">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14835">
          <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-14836">
            Upsell Configuration by Segment
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-14837">
          <table className="w-full" data-id="element-14838">
            <thead data-id="element-14839">
              <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-14840">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14841">
                  Upsell Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14842">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14843">
                  AI Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14844">
                  Human Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14845">
                  AI Commission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14846">
                  Best For
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14847">
                  <div className="flex items-center justify-center" data-id="element-14848">
                    <DollarSign size={14} className="text-emerald-500 mr-1" data-id="element-14849" />
                    <span data-id="element-14850">Whales</span>
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full" data-id="element-14851">
                      VIP
                    </span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14852">
                  <div className="flex items-center justify-center" data-id="element-14853">
                    <Droplet size={14} className="text-blue-500 mr-1" data-id="element-14854" />
                    <span data-id="element-14855">Dolphins</span>
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full" data-id="element-14856">
                      Spender
                    </span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14857">
                  <div className="flex items-center justify-center" data-id="element-14858">
                    <Fish size={14} className="text-cyan-500 mr-1" data-id="element-14859" />
                    <span data-id="element-14860">Minnows</span>
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full" data-id="element-14861">
                      Low Spender
                    </span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14862">
                  <div className="flex items-center justify-center" data-id="element-14863">
                    <Wallet size={14} className="text-gray-500 mr-1" data-id="element-14864" />
                    <span data-id="element-14865">Non-Spenders</span>
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full" data-id="element-14866">
                      Newbie
                    </span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14867">
                  <div className="flex items-center justify-center" data-id="element-14868">
                    <AlertCircle size={14} className="text-red-500 mr-1" data-id="element-14869" />
                    <span data-id="element-14870">Churn Risks</span>
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full" data-id="element-14871">
                      Dormant
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-14872">
              {upsellTypes.map(upsell => <tr key={upsell.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-14873">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-14874">
                    <div className="flex items-center" data-id="element-14875">
                      <div className="flex-shrink-0" data-id="element-14876">
                        <div className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-14877">
                          {upsell.icon}
                        </div>
                      </div>
                      <div className="ml-3" data-id="element-14878">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14879">
                          {upsell.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14880">
                    ${upsell.revenue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14881">
                    <div className="flex items-center" data-id="element-14882">
                      <Bot className="w-4 h-4 text-indigo-500 mr-1" data-id="element-14883" />
                      <span data-id="element-14884">${upsell.aiRevenue.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14885">
                    <div className="flex items-center" data-id="element-14886">
                      <User className="w-4 h-4 text-green-500 mr-1" data-id="element-14887" />
                      <span data-id="element-14888">${upsell.humanRevenue.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14889">
                    ${(upsell.aiRevenue * aiCommissionRate).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14890">
                    <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 rounded-full" data-id="element-14891">
                      {upsell.bestFor}
                    </span>
                  </td>
                  {['whales', 'dolphins', 'minnows', 'non-spenders', 'churn-risks'].map(segment => <td key={segment} className="px-6 py-4 whitespace-nowrap text-center" data-id="element-14892">
                      <div className="flex flex-col items-center space-y-2" data-id="element-14893">
                        <label className="relative inline-flex items-center cursor-pointer" data-id="element-14894">
                          <input type="checkbox" checked={upsellActive[upsell.id][segment]} onChange={() => handleToggleUpsell(upsell.id, segment)} className="sr-only peer" data-id="element-14895" />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600" data-id="element-14896"></div>
                        </label>
                        <div className="flex items-center text-xs" data-id="element-14897">
                          <Brain size={12} className="text-purple-500 mr-1" data-id="element-14898" />
                          <span className="text-gray-500 dark:text-gray-400" data-id="element-14899">
                            {upsell.algorithmConfidence[segment]}%
                          </span>
                        </div>
                        <select value={fallbackSettings[upsell.id][segment]} onChange={e => handleChangeFallback(upsell.id, segment, e.target.value)} className="block w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400" data-id="element-14900">
                          {fallbackMethods.map(method => <option key={method.id} value={method.id} data-id="element-14901">
                              {method.label}
                            </option>)}
                        </select>
                      </div>
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-14902">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14903">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14904">
            <Info size={18} className="text-blue-500 mr-2" data-id="element-14905" />
            Algorithmic Targeting vs Fallback Logic
          </h3>
        </div>
        <div className="p-6" data-id="element-14906">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-14907">
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4" data-id="element-14908">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-14909">
                <Brain size={16} className="text-purple-500 mr-2" data-id="element-14910" />
                Algorithmic Targeting
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-14911">
                The system analyzes user behavior to determine the optimal
                upsell to show at the perfect moment. Signals include:
              </p>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 list-disc pl-5" data-id="element-14912">
                <li data-id="element-14913">Chat history and content analysis</li>
                <li data-id="element-14914">Ritual completion patterns</li>
                <li data-id="element-14915">Previous purchase history</li>
                <li data-id="element-14916">Session frequency and duration</li>
                <li data-id="element-14917">Engagement with similar content</li>
              </ul>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4" data-id="element-14918">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-14919">
                <div size={16} className="text-indigo-500 mr-2" data-id="element-14920" />
                Fallback Logic
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-14921">
                When algorithm confidence is below threshold, the system falls
                back to one of these methods:
              </p>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-14922">
                <li className="flex items-center" data-id="element-14923">
                  <div size={14} className="text-indigo-500 mr-2 flex-shrink-0" data-id="element-14924" />
                  <span data-id="element-14925">
                    <strong data-id="element-14926">Lookalike Audiences:</strong> Target users similar
                    to known buyers
                  </span>
                </li>
                <li className="flex items-center" data-id="element-14927">
                  <Zap size={14} className="text-amber-500 mr-2 flex-shrink-0" data-id="element-14928" />
                  <span data-id="element-14929">
                    <strong data-id="element-14930">Broad Targeting:</strong> Show to wide audience
                    until signals gathered
                  </span>
                </li>
                <li className="flex items-center" data-id="element-14931">
                  <Droplet size={14} className="text-blue-500 mr-2 flex-shrink-0" data-id="element-14932" />
                  <span data-id="element-14933">
                    <strong data-id="element-14934">Context Fallback:</strong> Based on device,
                    location, time-of-day
                  </span>
                </li>
                <li className="flex items-center" data-id="element-14935">
                  <AlertCircle size={14} className="text-red-500 mr-2 flex-shrink-0" data-id="element-14936" />
                  <span data-id="element-14937">
                    <strong data-id="element-14938">Persona Fallback:</strong> Last resort using basic
                    personas
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-6 bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg" data-id="element-14939">
            <div className="flex items-start" data-id="element-14940">
              <div className="flex-shrink-0 mt-1" data-id="element-14941">
                <AlertCircle size={18} className="text-amber-500" data-id="element-14942" />
              </div>
              <div className="ml-3" data-id="element-14943">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14944">
                  Hybrid Routing Logic
                </h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-14945">
                  When algorithm confidence falls below segment-specific
                  thresholds, the system automatically switches to the
                  configured fallback method. AI handles all new and low-value
                  customers, while high-value upsells are routed to human
                  operators after the $120 lifetime spend threshold is reached.
                </p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-14946">
                  <strong data-id="element-14947">Example:</strong> For a Whale with 60% algorithm
                  confidence on Supplements upsell, the system would use
                  Lookalike targeting instead of algorithmic targeting if the
                  threshold is set to 70%.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};